/**
  * @description
  *
  * # Purpose
  * This is a base class for Mosy custom web elements. All other element classes shall inherit
  * from this class or one of its derivates.
  * This class is not usually used directly.
  *
  * # Use
  *
  * # Implementation notes
  * The class encapsulates all details on creation of custom element.
  * Custom element shall implement:
  * init() - will be called once, used for initialization of element
  *   - should call super.init() when finished
  * <property_name>Changed() - methods that will be invoked on change of particular property/attribute
  * <property_name>Store - if defined, will be called to store value of the property
  * <property_name>Retrieve - if defined, will be called to retrieve value of the property
  * <property_name>FromString - if defined, will be called to convert property value to string representation
  * <property_name>ToString - if defined, will be called to convert string into property value
  *
  * Elements may also use:
  * this._uuid to get unique web element ID
  * this._values - hash of internal element's state
  * this.js - getter/setter for object's properties and children as JavaScript
  * this.json - getter/setter for object's properties and children as JSON string
  * this.<attribute_name> - getting/setting will change the property and attribute with the same name
  * this.created to get epoch time of element's creation
  * this.updated to get epoch time of element's last update
  * this.language to get current language
  * this.behavior to get current behavior
  * this.theme to get current theme
  * this.body is getter/setter for main content of element
  */

const MAX_ATTRIBUTE_VALUE_LENGTH = 100
const OVERSIZED_ATTRIBUTE_VALUE_PLACEHOLDER = '⌗'
const RE_INTERPOLATE_OWN_PROPERTY = /\$\{(\w+(?:\.\w+)*)\}/g
const RE_INTERPOLATE_OTHER_PROPERTY = /@\{(.*?)\}/g
const MAX_INIT_TIME = 1000

let meleElement_uuid = 0

const PROPERTIES = _.extend({}, {
  key: { description: 'identifier of the element', type: 'string' },
  class: { description: 'get/set class of the element', type: 'string', noattribute: true },
  hidden: { description: 'if true, element shall not be displayed', type: 'boolean' },
  disabled: { description: 'if true, element will be dimmed and onclick will not work', type: 'boolean' },
  wait: { description: 'if not empty, dims the element and displays overlay with provided string', type: 'string' },
  waiticon: { description: 'icon for wait overlay', type: 'string', default: 'sys.wait' },
  compute: { description: 'function called by mele-container on change of other input', onlyflag: true },
  recompute: { description: 'function called by mele-container on own change', onlyflag: true },
  width: { type: 'string', description: 'Sets the width of the element in the window, regardless of its type. Options for width: 1, 1/2, 1/3, 1/4' },
}, HTMLElement._properties)

export default class meleElement extends HTMLElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * default constructor
  * generates unique UUID for the
  */
  constructor(self) {
    self = super(self)
    self._uuid = (++meleElement_uuid)
    self._values = {}
    self._constructed = Date.now()
    self._initialized = new Promise((resolve)=>{ self._initialized_resolve=resolve })

    // this stores dependencies between properties
    self._dependencies = { language: {} }

    // create getters/setters for attributes
    const attributes = self.constructor._properties
    for(const attr in attributes) {
      if(attributes[attr].type!=='function') {
        self._setupGetSet(attr)
      }
    }

    // registers event handlers
    const on_props = {}
    let obj = self
    do {
      const pp = Object.getOwnPropertyNames(obj)
      for(let i=0; i<pp.length; i++) {
        if(/^on\w+/.test(pp[i])) on_props[pp[i]]=1
      }
    } while ((obj = Object.getPrototypeOf(obj)) && obj.constructor!== HTMLElement)

    for(const prop in on_props) {
      if(typeof self[prop]==='function') {
        self.addEventListener(prop.substr(2), event => self[prop](event, self))
      }
    }

    self.created = self.updated = Date.now()

    return self
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * signature of element - string identification
  */
  signature() {
    let str = this.tagName
    const key = this.key
    if(typeof (key)==='string' && key.length>0) str += '@'+key
    return str
  } // signature

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * helper method to setup a property
  */
  _setupGetSet(attr) {
    const me=this
    Object.defineProperty(this, attr, {
      get: function() { return me.getProperty(attr) },
      set: function(value) { return me.setProperty(attr, value) },
    })
  } // _setupGetSet

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Returns true when a disconnectedCallback is firing because the element was
  * moved into a DocumentFragment by mmodModule's fragment-park (i.e. the
  * owning module was backgrounded), not because the element is truly being
  * removed. Subclasses that perform real teardown in disconnectedCallback
  * (releasing native resources, revoking blob URLs, exiting listeners) must
  * short-circuit when this returns true, otherwise the parked subtree will
  * be destroyed on every focus switch.
  */
  isParkedDisconnect() {
    const root = this.getRootNode()
    return !!(root && root.nodeType === 11) // Node.DOCUMENT_FRAGMENT_NODE
  } // isParkedDisconnect

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handles insertion of element into the DOM
  * Calls init() method, which shall:
  * - return true if synchronously finished initialization
  * - return false and call provided callback function when finished asynchronous initialization
  * After that attributeChanged will be called for all already set properties.
  */
  connectedCallback() {
    if(this._ready || this._initializing) return
    this._initializing = true

    // append list of inheritance into "class" attribute
    let proto = this.constructor
    const chain = [proto.name]
    while(proto.name!=='meleElement' && (proto = Object.getPrototypeOf(proto))!=null && proto.name!=='' && proto.name!=='meleElement') {
      chain.unshift(proto.name)
    }
    for(let className of chain) {
      className = String(className).toLowerCase().replace(/^mele/, 'mele-')
      this.classList.add(className)
    }

    // initialize
    if(typeof this.init === 'function') { this.init() }
    this._ready = true
    this._initializing = false

    // apply property sets that arrived while this element lived in a parked
    // (fragment-rooted) subtree and could not init - see setProperty
    if(this._pending_sets) {
      const sets = this._pending_sets
      delete this._pending_sets
      for(const args of sets) this.setProperty(...args)
    }

    // const props = this.constructor._properties || {}
    // for (const [prop, def] of Object.entries(props)) {
    //   if (!def) continue
    //   // skip flags without a truthy default, or properties that should not be attributes
    //   if (def.noattribute) continue
    //   if (def.onlyflag && !def.default) continue

    //   const hasAttr = this.hasAttribute(prop)
    //   const hasValue = typeof this._genericRetrieve(prop) !== 'undefined'

    //   if (!hasAttr && !hasValue && Object.prototype.hasOwnProperty.call(def, 'default')) {
    //   // force write even if old==new; don't bump updated time
    //     this.setProperty(prop, def.default, undefined, true, /*skip_update_time*/ true)
    //   }
    // }
  } // connectedCallback

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  * Derived classes shall overwrite this method to create internal elements
  * After that attributeChanged will be called for all already set properties.
  */
  init() {
    if(this._initialized_resolve) {
      this._initialized_resolve()
      delete this._initialized_resolve
    }
  } // init

  // transform given value to require value type
  _coerceValue(type, value) {
    if(type==='boolean') {
      return(!(value===null || value===undefined || value===false || value===0 || value==='0'))
    }else if(type==='array') {
      return(
        Array.isArray(value) ? value :
          value===null || value===undefined ? [] :
            [value]
      )
    }else if(type==='object') {
      return(
        value===null || value===undefined ? {} :
          typeof value === 'object' ? value :
            {}
      )
    }else if(type==='string') {
      if(value && typeof value==='object') value = JSON.stringify(value)
      return String(value ?? '')
    }else if(type==='number' || type==='float') {
      value = parseFloat(value)
      return isNaN(value) ? '' : value
    }else if(type==='integer') {
      value = parseInt(value)
      return isNaN(value) ? '' : value
    }else{
      return value
    }
  } // _coerceValue

  // generic store of value using this._values hash
  _genericStore(prop, value) {
    if(typeof (value)==='undefined') {
      delete this._values[prop]
    }else {
      this._values[prop] = value
    }
  } // _genericStore

  // generic retrieve of value using this._values hash
  _genericRetrieve(prop) {
    const val = this._values[prop]
    // if(prop=='key' && typeof(val)=='undefined'){
    //   val = this.getAttribute('key');
    // }
    return val
  } // _genericRetrieve

  // used for conversion from any type of property value for DOM attribute representation (string-only)
  _genericToString(type, value) {
    let str
    if(type==='boolean') {
      // boolean value is represented as FALSE=null (i.e. attribute is missing) and TRUE='' (i.e. attribute is present without value)
      str = value ? '' : null
    }else if(type==='array' || type==='object') {
      str = JSON.stringify(value)
    }else if(type==='stringorarray') {
      str =
        value===null || value===undefined ? null :
          typeof value==='string' ? value :
            JSON.stringify(value)
    }else{
      // to make it more readable, we return null for empty strings - then attribute will not be present at all
      value = String(value??'')
      str = value==='' ? null : value
    }

    // too big values are represented by a shortcut
    if(typeof str==='string' && str.length>MAX_ATTRIBUTE_VALUE_LENGTH) {
      str = OVERSIZED_ATTRIBUTE_VALUE_PLACEHOLDER
    }
    return str
  } // _genericToString

  _genericFromString(type, str) {
    if(str===OVERSIZED_ATTRIBUTE_VALUE_PLACEHOLDER) return undefined
    if(type==='boolean') {
      // boolean value is represented as FALSE=null (i.e. attribute is missing) and TRUE='' (i.e. attribute is present without value)
      return !(str===null || str===undefined || str===false || str===0 || str==='0')
    }else if(type==='array' || type==='object') {
      let value = null
      try{ value = JSON.parse(str) }catch(e) {}
      return value
    }else if(type==='stringornull') {
      return str===null || str===undefined ? null : String(str)
    }else if(type==='stringorarray') {
      str = String(str??'')
      try{
        str = JSON.parse(str)
        return str
      }catch(e) {
        return str
      }
    }else{
      return String(str??'')
    }
  } // _genericFromString

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * standard getter
  */
  getProperty(prop) {
    const def = this.constructor._properties[prop]
    if(!def) return

    // retrieve value
    let value = typeof this[prop+'Retrieve'] === 'function' ? this[prop+'Retrieve']() : this._genericRetrieve(prop)

    // if no value, use default
    if(typeof value==='undefined' || (def.type==='string' && value==='')) {
      value = def.default
    }

    return this._coerceValue(def.type, value)
  } // getProperty

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * standard setter
  */
  setProperty(prop, value, context_for_changed_callback, force_if_notchanged, skip_update_time) {
    const def = this.constructor._properties[prop]
    if(!def) return

    // wait until element is ready
    if(!this._ready) {
      // an element created inside a parked subtree (fragment-park, mmodModule._setParked)
      // stays disconnected until its module is unparked, so init/_ready can be arbitrarily
      // far away - the retry timer would spin and end in a NOT CONSTRUCTED error while the
      // value got lost. Queue the set instead; connectedCallback applies it after init.
      if(this.isParkedDisconnect()) {
        if(!this._pending_sets) this._pending_sets = []
        this._pending_sets.push([prop, value, context_for_changed_callback, force_if_notchanged, skip_update_time])
        return
      }
      if(Date.now() - this._constructed > MAX_INIT_TIME) {
        console.error(this.constructor.name.toUpperCase()+' '+this._uuid+' NOT CONSTRUCTED WITHIN '+MAX_INIT_TIME+' MS')
        return
      }
      setTimeout(()=>{
        this.setProperty(prop, value, context_for_changed_callback, force_if_notchanged, skip_update_time)
      }, 1)
      return
    }

    value = this._coerceValue(def.type, value)
    const old = this.getProperty(prop)
    if(force_if_notchanged || !_.isEqual(old, value)) {
      // update timestamp
      if(!skip_update_time) {
        this.updated = Date.now()
      }

      // store the value
      if(typeof this[prop+'Store'] === 'function') {
        this[prop+'Store'](value)
      }else {
        this._genericStore(prop, value)
      }

      // update DOM attribute with the same name
      if(prop==='class') {
        // no't update class attribute
      }else if(def.noattribute) {
        this.removeAttribute(prop)
      }else if(def.onlyflag) {
        if(value===null || value===undefined || (def.type==='boolean' && !value)) {
          this.removeAttribute(prop)
        }else {
          this.setAttribute(prop, '')
        }
      }else {
        const str = typeof this[prop+'ToString'] === 'function' ? this[prop+'ToString'](value) : this._genericToString(def.type, value)
        if(str===null || str===undefined) {
          this.removeAttribute(prop)
        }else {
          this.setAttribute(prop, str)
        }
      }

      // notify on change
      if(typeof this[prop+'Changed'] === 'function') {
        this[prop+'Changed'](value, old, context_for_changed_callback)
      }
    }
    return value
  } // setProperty

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handles change of attribute
  */
  attributeChangedCallback(prop, oldStr, newStr) {
    if(prop==='class') return // ignore changes to class
    const def = this.constructor._properties[prop]
    if(!def) return
    if(def.onlyflag) return
    if(newStr===OVERSIZED_ATTRIBUTE_VALUE_PLACEHOLDER) return // attribute is oversized, so it had to be set by JavaScript, so there is no need to process the change
    if(def.noattribute && newStr==null) return // we are hidden and empty, do nothing
    if(def.readonly) return // attribute change not allowed
    const value = typeof this[prop+'FromString'] === 'function' ? this[prop+'FromString'](newStr) : this._genericFromString(def.type, newStr)
    const old = this.getProperty(prop)
    if(!_.isEqual(old, value)) {
      this.setProperty(prop, value)
    }
  } // attributeChangedCallback

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * translate text using current language
  */
  translate(text, empty_if_no_translation, dependent_property) {
    text = String(text??'')
    if(text==='') return ''

    // setup dependency on language change if text to be translated is not empty
    dependent_property = String(dependent_property??'')
    if(dependent_property) this._dependencies.language[dependent_property] = 1

    const text2 = this._interpolateVariables(text, dependent_property)
    let text3 = LANG.translate(this.language, text2, true)
    if(text3==='') {
      if(empty_if_no_translation || /^\w+:\$/.test(text2) || text2.replace(/{{\w+:}}/g, '')==='') {
        return ''
      }else {
        text3 = text2
      }
    }
    text3 = text3.replace(/\u200B/g, '') // ZERO WIDTH SPACE
    if(/∫/.test(text3)) {
      text3 = text3.split('∫').map((fragment)=>{ return '<span>'+fragment+'</span>' }).join('')
    }
    return text3
  } // translate

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * translate text using current language and append this._dependencies as they appear in the text
  */
  translateAndSetDependencies(text, empty_if_no_translation, dependent_property) {
    this.clearDependencies(dependent_property)
    text = this.translate(text, empty_if_no_translation, dependent_property)
    return String(text ?? '')
  } // translateAndSetDependencies

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * translate text using current language and set this._dependencies as they appear in the text
  */
  translateAndAppendDependencies(text, empty_if_no_translation, dependent_property) {
    text = this.translate(text, empty_if_no_translation, dependent_property)
    return String(text??'')
  } // translateAndAppendDependencies

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * just interpolates variables and append this._dependencies['@'] if needed
  */
  interpolateAndSetDependencies(text, empty_if_no_translation, dependent_property) {
    text = String(text??'')
    if(text==='') return ''

    dependent_property = String(dependent_property??'')
    this.clearDependencies(dependent_property)

    return this._interpolateVariables(text, dependent_property)
  } // interpolateAndSetDependencies

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * clear this._dependencies for the given property
  */
  clearDependencies(dependent_property) {
    dependent_property = String(dependent_property??'')
    if(dependent_property==='') return
    for(const dep in this._dependencies) { delete this._dependencies[dep][dependent_property] }
  } // clearDependencies

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * translate text using current language and set this._dependencies as they appear in the text
  */
  _interpolateVariables(text, dependent_property) {
    // this part is to perform extremely fast replacement of the most common case: ${key}
    // eslint-disable-next-line
    const idx = text.indexOf('${key}')
    if(idx>=0) {
      text = text.substr(0, idx)+String(this._values.key??'')+text.substr(idx+6)
      if(dependent_property) {
        if(!this._dependencies.key) this._dependencies.key = {}
        this._dependencies.key[dependent_property] = 1
      }
      if(text.indexOf('{')<0) return text
    }

    if(text.indexOf('${')>=0) {
      text = text
        .replace(RE_INTERPOLATE_OWN_PROPERTY, (m, m1)=>{
          const keys = m1.split('.')
          let value = this

          // set dependency on reference to other property
          if(dependent_property) {
            if(!this._dependencies[keys[0]]) this._dependencies[keys[0]] = {}
            this._dependencies[keys[0]][dependent_property] = 1
          }

          // retrieve value
          while(keys.length>0) {
            if(!value || typeof value!=='object') {
              value = undefined
              break
            }
            value = value[keys.shift()]
          }

          // booleans are always returned as 0 or 1
          if(typeof value==='boolean') {
            return(value ? 1 : 0)
          }else {
            return String(value??'')
          }
        })
    }

    if(text.indexOf('@{')>=0) {
      text = text
        .replace(RE_INTERPOLATE_OTHER_PROPERTY, (m, m1)=>{
          // set dependency on reference to other property
          if(dependent_property) {
            if(!this._dependencies['@']) this._dependencies['@'] = {}
            this._dependencies['@'][dependent_property] = 1
          }

          // replace with value of the input field with key matching the reference
          const holder = this.closest('.mele-container:not([propagate])')
          const el = holder.find('.mele-input@{'+m1+'}')
          return String(el?.value??'')
        })
    }

    return text
  } // _interpolateVariables

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * returns translations matching given prefix (without the prefix itself)
  */
  getOptions(prefix) {
    return LANG.translateOptions(this.language, prefix)
  } // getOptions

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Return outerHTML of DOM element with given ID
  */
  getTemplate(element_id) {
    const icons = this.ownerDocument.getElementById('icons').content
    let el = icons.getElementById(element_id)
    if(!el) return
    el = el.cloneNode(true)
    el.removeAttribute('id')
    return el
  } // getTemplate

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Return outerHTML of DOM element with given ID
  */
  getImage(src, dependent_property) {
    src = String(src??'')
    if(src==='') return

    // setup dependency on language change if text to be translated is not empty
    dependent_property = String(dependent_property??'')
    if(dependent_property) this._dependencies.language[dependent_property] = 1

    src = this._interpolateVariables(src, dependent_property)
    const src_translated = LANG.translate(this.language, src, true)

    let res = null
    const icons = this.ownerDocument.getElementById('icons').content
    if(icons.getElementById(src_translated)) {
      res = this.getTemplate(src_translated)
    }else if(icons.getElementById(src)) {
      res = this.getTemplate(src)
    } else if((/\.(jpe?g|gif|png|svg)$/.test(src))) {
      res = this.createImageNode(src)
    } else if(/\.(jpe?g|gif|png|svg)$/.test(src_translated)) {
      res = this.createImageNode(src_translated)
    } else if(/base64/.test(src_translated)) {
      res = this.createElementFast('img')
      res.src = /^data:/.test(src_translated) ? src_translated: 'data:'+src_translated
    } else if(/base64/.test(src)) {
      res = this.createElementFast('img')
      res.src = /^data:/.test(src) ? src: 'data:'+src
    } else if(/<svg/.test(src)) {
      let div = this.createElementFast('div')
      div.innerHTML = src
      const svg = div.children[0]
      if(svg) res = svg
      div.innerHTML = ''
      div = null
    }
    return res
  } // getImage

  createImageNode(src) {
    const el = this.createElementFast('img')
    el.src = src
    return el
  } // createImageNode

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates new
  */
  setSvgIcon(id, svg) {
    const doc = this.ownerDocument
    const icons = doc.getElementById('icons').content
    if(!icons) return
    const old_icon = icons.getElementById(id)
    if(old_icon) old_icon.deleteElement()
    const div = doc.createElement('div')
    div.innerHTML = svg
    if(div.firstChild) {
      const icon = icons.appendChild(div.firstChild)
      icon.id = id
    }
  } // setSvgIcon

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Return outerHTML of DOM element with given ID
  */
  getImageAndSetDependencies(src, dependent_property) {
    this.clearDependencies(dependent_property)
    return this.getImage(src, dependent_property)
  } // getImageAndSetDependencies

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * when wait change, add/remove wait cover-up
  */
  waitChanged() {
    if(this.wait) {
      if(!this._wait_cover) {
        this._wait_cover = this.appendChildren({
          tag: 'wait',
          children: [{ tag: 'mele-image' }, { tag: 'mele-text' }],
        })
      }
      this._wait_cover.forAll('mele-image', el=>{ el.icon=this.waiticon })
      this._wait_cover.forAll('mele-text', el=>{ el.label=this.wait })
    }else if(!this.wait && this._wait_cover) {
      this._wait_cover.deleteElement()
      delete this._wait_cover
    }
  } // waitChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * changed waiticon
  */
  waiticonChanged() {
    if(this._wait_cover) {
      this._wait_cover.forAll('mele-image', el=>{ el.icon=this.waiticon })
    }
  } // waiticonChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * when language change, simulate change of all language dependent attributes
  */
  languageChanged() {
    this.invokeDependentChanges('language')
  } // languageChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Re-translates element
  */
  retranslate() {
    this.languageChanged()
  } // retranslate

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * invokes xxxChanged() for all properties dependent on given prop
  */
  invokeDependentChanges(prop) {
    if(!this._dependencies[prop]) return
    for(const dependent in this._dependencies[prop]) {
      try{
        const current_value = this[dependent]
        this[dependent+'Changed'](current_value, current_value)
      }catch(e) {}
    }
  } // invokeDependentChanges

  getElementJS() {
    const json = this.getProperties()
    json.tag = String(this.tagName??'').toLowerCase()
    return json
  } // getElementJS

  /**
  * retrieves properties of the custom element
  * overwrites default Element.prototype.getProperties that works for generic elements
  */
  getProperties() {
    const json = {}
    const props = this.constructor._properties || {}
    for(const prop in props) {
      const val = this[prop]
      if(val!==undefined && val!==null && val!=='' && val!==false) {
        json[prop] = val
      }
    }
    return json
  } // getProperties

  /**
  * set properties of the custom element
  */
  setProperties(json, no_removal) {
    if(!json || typeof json!=='object') return

    // store method replacements first
    const props = this.constructor._properties || {}
    for(const prop in json) {
      if(prop!=='tag' && props[prop]?.type==='function') {
        this[prop] = json[prop]
      }
    }

    // then save "key"
    if(typeof json.key !== 'undefined') {
      this.key = json.key
    }

    // remove any obsolete existing properties
    if(!no_removal) {
      for(const prop in props) {
        if(!props[prop].readonly && props[prop].type!=='function' && typeof json[prop]==='undefined') {
          this[prop] = undefined
        }
      }
    }

    // create/replace given properties
    for(const prop in json) {
      if(prop!=='tag' && prop!=='key' && props[prop]?.type!=='function') {
        try{
          this[prop] = json[prop]
        }catch(e) {
        }
      }
    }

    return true
  } // setProperties

  classStore(value) {
    this.className = value

    // append list of inheritance into "class" attribute
    let proto = this.constructor
    const chain = [proto.name]
    while(proto.name!=='meleElement' && (proto = Object.getPrototypeOf(proto))!=null && proto.name!=='' && proto.name!=='meleElement') {
      chain.unshift(proto.name)
    }
    for(let className of chain) {
      className = String(className).toLowerCase().replace(/^mele/, 'mele-')
      this.classList.add(className)
    }
  } // classStore

  classRetrieve(value) {
    return this.className
  } // classRetrieve

  simplify() {
    return this
  } // simplify

  classScope(suffix) {
    const ns = this.constructor.name.replace(/^mele/, '').toLowerCase()
    // if(this.parentElementName) ns = this.parentElementName + '-' + ns
    // if(suffix) ns = ns + '-' + suffix
    return suffix ? ns + '-' + suffix : ns
  }
} // class meleElement

meleElement._properties = PROPERTIES
meleElement.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-element', meleElement)
